/**
 * The app sidebar (Beta-1 plan P6-07 / P6-08, Tim's D10). Two sidebars, one component:
 *   - REPSit (platform pages): Dashboard · ACCESS LEVELS · REPSit LEARNING · INTELLIGENT ASSISTANTS · ADMIN
 *   - DREW (inside the IA): avatar plate · OE Dashboard · ACCESS LEVELS · OE INPUT/ASSESSMENT ·
 *     DATA REVIEW/COMPARISON · MAIN
 * The lists live in `src/platform/navigation/navigation.js` (pure, unit-tested); this file only
 * renders them, gated by the viewer's persona (`accessControl.canAccessToolPath`).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Tooltip, Collapse, Typography, Box, useTheme } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CodeIcon from '@mui/icons-material/Code';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DescriptionIcon from '@mui/icons-material/Description';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import StorageIcon from '@mui/icons-material/Storage';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import GavelIcon from '@mui/icons-material/Gavel';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SpeedIcon from '@mui/icons-material/Speed';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useDemoRole } from '../../contexts/DemoRoleContext';
import { canAccessToolPath } from '../../assistants/drew/utils/accessControl';
import { getAssistant } from '../../assistants/assistants';
import { FEATURE_HUGO } from '../../config/features';
import { SIDEBAR_MODES, activeSectionFor, canSeeApi, drewSidebar, filterSidebar, isItemActive, repsitSidebar, sidebarModeFor } from '../../platform/navigation/navigation.js';

const ICONS = {
  Home: HomeIcon,
  Settings: SettingsIcon,
  HelpOutline: HelpOutlineIcon,
  Code: CodeIcon,
  Dashboard: DashboardIcon,
  Assignment: AssignmentIcon,
  Assessment: AssessmentIcon,
  MenuBook: MenuBookIcon,
  Description: DescriptionIcon,
  AdminPanelSettings: AdminPanelSettingsIcon,
  Engineering: EngineeringIcon,
  SupervisorAccount: SupervisorAccountIcon,
  AccountTree: AccountTreeIcon,
  BusinessCenter: BusinessCenterIcon,
  Storage: StorageIcon,
  RuleFolder: RuleFolderIcon,
  Gavel: GavelIcon,
  LocationCity: LocationCityIcon,
  Public: PublicIcon,
  Language: LanguageIcon,
  CompareArrows: CompareArrowsIcon,
  Speed: SpeedIcon,
  HealthAndSafety: HealthAndSafetyIcon,
  Article: ArticleIcon,
  AddCircleOutline: AddCircleOutlineIcon,
};

/**
 * One colour per section LEVEL so people can see what falls under what (Alex, 2026-09-21: "it's
 * confusing for me and I built it"). Brand-compatible, deliberately muted; DREW itself keeps the
 * indigo badge (P6-05) and there is still no per-IA accent (P6-06 not adopted).
 */
const SECTION_TONES = {
  home: { color: '#1E2360', dark: '#C7CBF0', short: 'HOME' },
  access: { color: '#16699F', dark: '#6FB1E0', short: 'ROLES' },
  learning: { color: '#0B5443', dark: '#7BD39A', short: 'LEARN' },
  assistants: { color: '#854F0B', dark: '#F3C06A', short: 'IAs' },
  admin: { color: '#6B6963', dark: '#A9A69E', short: 'ADMIN' },
  input: { color: '#0B5443', dark: '#7BD39A', short: 'INPUT' },
  review: { color: '#16699F', dark: '#6FB1E0', short: 'REVIEW' },
  main: { color: '#6B6963', dark: '#A9A69E', short: 'MAIN' },
};
const toneFor = (key, mode = 'light') => {
  const t = SECTION_TONES[key] || { color: '#6B6963', dark: '#A9A69E', short: (key || '').slice(0, 6).toUpperCase() };
  return { ...t, color: mode === 'dark' ? t.dark : t.color };
};

function ItemIcon({ item, active }) {
  if (item.avatar) {
    return (
      <Box
        component="img"
        src={item.avatar}
        alt=""
        sx={{ width: 26, height: 26, borderRadius: '50%', filter: item.disabled ? 'grayscale(1)' : 'none', opacity: item.disabled ? 0.55 : 1 }}
      />
    );
  }
  const Icon = ICONS[item.icon] || DashboardIcon;
  return <Icon color={active ? 'primary' : undefined} />;
}

function Sidebar({ open, width, expandedWidth = 284, isMobile, onClose }) {
  const navigate = useNavigate();
  // Rail "peek": hovering the collapsed rail slides the full menu over the page (names visible)
  // without moving the content; leaving it slides back. Clicking ☰ still pins it open.
  const paletteMode = useTheme().palette.mode;
  const [peek, setPeek] = useState(false);
  const peekTimer = useRef(null);
  const expanded = isMobile ? open : open || peek;
  const startPeek = () => { if (!open && !isMobile) { clearTimeout(peekTimer.current); peekTimer.current = setTimeout(() => setPeek(true), 120); } };
  const endPeek = () => { clearTimeout(peekTimer.current); setPeek(false); };
  useEffect(() => { if (open) setPeek(false); }, [open]);
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  const { persona } = useDemoRole();
  const personaId = persona?.id;
  const pathname = location.pathname;

  const mode = sidebarModeFor(pathname);
  const sections = useMemo(() => {
    const showApi = canSeeApi(personaId);
    const raw = mode === SIDEBAR_MODES.drew ? drewSidebar({ featureHugo: FEATURE_HUGO, showApi }) : repsitSidebar({ isSuperAdmin: isSuperAdmin(), showApi });
    return filterSidebar(raw, (path) => canAccessToolPath(personaId, path));
  }, [mode, personaId, isSuperAdmin]);

  // Sections start open (unless `defaultClosed`); the one holding the current page is always open.
  const [closed, setClosed] = useState(() => {
    const all = [drewSidebar({ featureHugo: FEATURE_HUGO }), repsitSidebar({ isSuperAdmin: true })].flat();
    return Object.fromEntries(all.filter((s) => s.defaultClosed).map((s) => [s.key, true]));
  });
  const activeKey = activeSectionFor(sections, pathname);
  useEffect(() => {
    if (activeKey) setClosed((c) => (c[activeKey] ? { ...c, [activeKey]: false } : c));
  }, [activeKey]);
  const isOpen = (key) => !closed[key];
  const toggle = (key) => setClosed((c) => ({ ...c, [key]: !c[key] }));

  const go = (path) => {
    navigate(path);
    if (isMobile && onClose) onClose();
  };

  const renderItem = (item, tone, nested) => {
    const active = isItemActive(item, pathname);
    const tip = !expanded ? item.text : item.tooltip || '';
    return (
      <Tooltip key={item.id} title={tip} placement="right">
        <ListItem disablePadding>
          <ListItemButton
            selected={active}
            disabled={item.disabled}
            onClick={() => !item.disabled && item.path && go(item.path)}
            sx={{
              minHeight: 44,
              justifyContent: expanded ? 'initial' : 'center',
              pl: expanded && nested ? 3.25 : 2.5,
              pr: 2,
              opacity: item.disabled ? 0.55 : 1,
              position: 'relative',
              // the section's colour marks the active item; a faint guide line shows nesting
              borderLeft: 3,
              borderLeftColor: active ? tone.color : 'transparent',
              '&.Mui-selected': { bgcolor: `${tone.color}12` },
              '&.Mui-selected:hover': { bgcolor: `${tone.color}1e` },
              ...(expanded && nested && {
                '&::before': { content: '""', position: 'absolute', left: 17, top: 0, bottom: 0, width: '1px', bgcolor: 'divider', opacity: 0.8, pointerEvents: 'none' },
              }),
            }}
            aria-current={active ? 'page' : undefined}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 2 : 'auto', justifyContent: 'center', color: active ? tone.color : undefined, transition: 'margin 0.25s' }}>
              <ItemIcon item={item} active={active} />
            </ListItemIcon>
            <Box
              sx={{
                minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 0.5,
                opacity: expanded ? 1 : 0, maxWidth: expanded ? 220 : 0, overflow: 'hidden',
                transition: 'opacity 0.2s ease 0.05s, max-width 0.25s ease',
                whiteSpace: 'normal',
              }}
            >
              <ListItemText
                primary={item.text}
                secondary={item.secondary || null}
                primaryTypographyProps={{ variant: 'body2', fontWeight: active ? 700 : 500, sx: { lineHeight: 1.25 } }}
                secondaryTypographyProps={{ variant: 'caption', noWrap: true, sx: { display: 'block', lineHeight: 1.2 } }}
              />
              {item.disabled && <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 0.5, flexShrink: 0 }} />}
            </Box>
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  };

  const renderSection = (section) => {
    const tone = toneFor(section.key, paletteMode);
    if (!section.label) return section.items.map((it) => renderItem(it, tone, false));
    const sectionOpen = isOpen(section.key);
    return (
      <React.Fragment key={section.key}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => (expanded ? toggle(section.key) : startPeek())}
            sx={{ py: expanded ? 0.75 : 0.5, px: expanded ? 2.5 : 1, minHeight: 36, justifyContent: 'center', mt: 0.5 }}
            aria-expanded={sectionOpen}
            aria-label={section.label}
          >
            {expanded ? (
              <>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone.color, mr: 1, flexShrink: 0 }} />
                <Typography variant="overline" noWrap sx={{ flexGrow: 1, color: tone.color, fontWeight: 800, letterSpacing: 1, fontSize: '0.7rem', lineHeight: 1.4 }}>
                  {section.label}
                </Typography>
                {sectionOpen ? <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />}
              </>
            ) : (
              /* Collapsed rail: the section's short name in its colour — the category stays readable */
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <Box sx={{ width: '70%', borderTop: 2, borderColor: `${tone.color}55`, borderRadius: 1 }} />
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: 0.8, color: tone.color, lineHeight: 1 }}>
                  {tone.short}
                </Typography>
              </Box>
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={expanded ? sectionOpen : true} timeout={250} unmountOnExit>
          {section.items.map((it) => renderItem(it, tone, true))}
        </Collapse>
      </React.Fragment>
    );
  };

  const drew = getAssistant('drew');

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={isMobile ? { keepMounted: true } : undefined}
      onMouseEnter={startPeek}
      onMouseLeave={endPeek}
      sx={{
        width: isMobile ? (open ? width : 0) : width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isMobile ? width : expanded ? expandedWidth : width,
          boxSizing: 'border-box',
          mt: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s',
          overflowX: 'hidden',
          overflowY: 'hidden',
          ...(peek && !open && { boxShadow: 8 }),
        },
      }}
    >
      {/* Brand logo — the REPSit home: the entry page (P3-12 / P6-01) */}
      <Box onClick={() => go('/')} sx={{ flexShrink: 0, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, cursor: 'pointer' }}>
        <Box component="img" src="/repsit-logo.png" alt="REPSit" sx={{ height: expanded ? 40 : 30, width: 'auto', maxWidth: '100%', objectFit: 'contain', transition: 'height 0.3s' }} />
      </Box>
      <Divider />

      <List sx={{ pt: 1, flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }} aria-label={mode === SIDEBAR_MODES.drew ? 'DREW navigation' : 'REPSit navigation'}>
        {mode === SIDEBAR_MODES.drew && (
          /* Active Intelligent Assistant — avatar + name (Tim: "use the avatar here") */
          <Tooltip title={!expanded ? `DREW — ${drew.shortTitle}` : ''} placement="right">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: expanded ? 2 : 0, justifyContent: expanded ? 'flex-start' : 'center', pb: 1, mb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
              <Box component="img" src={drew.avatarSmall} alt="DREW" sx={{ width: expanded ? 40 : 30, height: expanded ? 40 : 30, flexShrink: 0, transition: 'width 0.3s, height 0.3s' }} />
              {expanded && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                    DREW
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {drew.shortTitle.replace(' System', '')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Tooltip>
        )}
        {sections.map(renderSection)}
      </List>
    </Drawer>
  );
}

export default Sidebar;
